import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactModal from "react-modal";

import useDictation, { MAX_MS } from "./useDictation.js";
import { playStartCue, playStopCue } from "./cues.js";
import { Button, ButtonGroup, Icon, IconButton } from "../components/ui";
import "./dictation-overlay.scss";

/**
 * DictationOverlay — the shared speech-to-candidates surface.
 *
 * Domain-agnostic on purpose: Request creation reuses it by passing a different
 * `schema` and `renderReview`. It knows about recording, a checklist and a
 * list of candidates; it knows nothing about listings, products or watches.
 *
 * ONE mode. There is no guided/free toggle: the backend always injects the
 * example sentence as a prompt hint and treats a rambling user identically, so
 * the switch changed the UI and nothing else. The sentence stays on screen as a
 * permanent hint — it is the single most useful thing on the panel — and it is
 * never a parser.
 *
 * The checklist shows only what a PERSON says. Mandatory form fields that are
 * derived (composed, or carried from the chosen product) are filled silently
 * and never appear here: they are not things the user failed to mention.
 *
 * Centred modal on desktop (the `rc-dup-modal` pattern), full-bleed bottom
 * sheet under 768px. react-modal supplies the focus trap and Escape.
 *
 * @param {object}   schema          see listingDictation.js for the contract
 * @param {function} upload          (blob, {signal}) => parsed response
 * @param {function} renderReview    (result, {onDone}) => node — the editable review step
 * @param {function} onSearchManually (result) => void
 * @param {function} onClose         (result) => void
 */

const mmss = (ms) => {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const STATUS_TEXT = {
  idle: "Ready to record.",
  "requesting-permission": "Waiting for microphone permission.",
  recording: "Recording.",
  encoding: "Recording stopped. Preparing audio.",
  uploading: "Analysing the recording and matching products.",
  review: "Finished. Review what we heard.",
  error: "Recording could not be used.",
};

const DictationOverlay = ({
  isOpen,
  onClose,
  schema,
  upload,
  renderReview,
  onSearchManually,
  reanalyse,
}) => {
  const {
    phase,
    errorKind,
    errorMessage,
    elapsedMs,
    nearingLimit,
    level,
    result: recorded,
    canRetry,
    start,
    stop,
    cancel,
    retry,
  } = useDictation({ upload });

  // A re-analysis of a corrected transcript REPLACES the recorded result, and
  // everything on the panel reads `result` — otherwise the transcript would say
  // one thing while the fields below still said another, with nothing to tell
  // the rep which one the listing gets built from.
  const [reanalysed, setReanalysed] = useState(null);
  const [reanalysing, setReanalysing] = useState(false);
  const [reanalyseError, setReanalyseError] = useState(null);
  const result = reanalysed ?? recorded;

  // Dismissing mid-flight aborts the request and keeps nothing. The result goes
  // out first: a rejected candidate list is what calibrates the score floor.
  const close = () => {
    onClose(result);
    cancel();
  };

  useEffect(() => {
    if (!isOpen) cancel();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoised: it feeds two useMemos, and a fresh {} each render defeats both.
  const fields = useMemo(() => result?.fields ?? {}, [result]);
  // NOTE: candidates / looseCandidates are deliberately not read here any more.
  // They were only ever used to answer "did anything match" for the review
  // checklist, which is gone; the candidate list itself belongs to the domain's
  // review panel, which reads it off `result`.
  const swept = phase === "review";

  const extras = useMemo(
    () =>
      Object.entries(fields)
        .filter(([, field]) => field?.value != null && field.value !== "")
        .map(([key, field]) => ({ key, label: schema.extraLabel(key), field }))
        .filter((e) => !!e.label),
    [fields, schema]
  );

  const heardMoney = useMemo(() => schema.heardMoney(fields), [fields, schema]);

  // Set once the domain has finished (the listing exists): the sheet then shows
  // nothing but the success state.
  const [done, setDone] = useState(false);
  useEffect(() => { if (phase !== "review") setDone(false); }, [phase]);

  // ── What was heard, and the rep's correction of it ────────────────────────
  // Speech-to-text mishears names and reference numbers, and the transcript is
  // what every field below was derived from — so it is editable. `edited` is
  // null until the rep changes something, which keeps "untouched" distinct from
  // "edited back to the original".
  const [edited, setEdited] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const draftRef = useRef(null);
  const transcript = edited ?? result?.transcript ?? "";

  // A NEW RECORDING replaces everything, so a stale correction and a stale
  // re-analysis must not survive into it. Keyed on the recorded usage id, not
  // on `result` — the re-analysis sets `result` itself and would clear its own
  // work on the very next render.
  useEffect(() => {
    setEdited(null);
    setEditing(false);
    setReanalysed(null);
    setReanalyseError(null);
  }, [recorded?.usageId]);
  useEffect(() => { if (editing) setDraft(transcript); }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (editing) draftRef.current?.focus(); }, [editing]);

  /**
   * Autosave happens ON COMPLETION of the edit — blur, Enter, or the tick — and
   * never per keystroke: re-analysing every character would be one Gemini call
   * per letter typed, and each one counts against the daily cap.
   */
  const commitDraft = async () => {
    const next = draft.trim();
    setEditing(false);

    // An empty box is a mis-edit, not an instruction to forget the transcript.
    if (!next || next === transcript) return;

    setEdited(next);
    if (!reanalyse) return;

    setReanalysing(true);
    setReanalyseError(null);
    try {
      const fresh = await reanalyse(next);
      // The server echoes the submitted text back as the transcript, so the box
      // keeps showing exactly what was typed.
      setReanalysed(fresh);
      setEdited(null);
    } catch (e) {
      // The edit is KEPT on failure — discarding what the rep just typed
      // because a network call failed is the worst of both outcomes. The fields
      // below are still the ones from the recording, and this says so.
      setReanalyseError(
        e?.userFacing
          ? e.message
          : "Couldn't re-read that. The fields below still come from the recording."
      );
    } finally {
      setReanalysing(false);
    }
  };

  const recording = phase === "recording";
  const busy = phase === "encoding" || phase === "uploading" || phase === "requesting-permission";
  const capturing = phase !== "review" && phase !== "error" && !done;
  const analysing = phase === "uploading";

  // The cue announces the rep's own action, so it fires on the click rather than
  // on the phase change: `start` is async (permission, then capture) and a beep
  // that waits for the phase would land after the first spoken words. `stop` is
  // paired with it for symmetry — cueing one end only is worse than neither.
  const startWithCue = () => { playStartCue(); start(); };
  const stopWithCue = () => { playStopCue(); stop(); };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={close}
      className="rc-dictate-sheet"
      overlayClassName="rc-dictate-overlay"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={!busy}
    >
      <div className="rc-dictate-sheet__grab" aria-hidden="true" />

      <div className="rc-dictate-sheet__head">
        <h2 className="rc-dictate-sheet__title">{schema.title}</h2>
        <IconButton
          variant="ghost"
          size="xl"
          round
          className="rc-dictate-sheet__close"
          label="Close"
          data-btn="dictation-cancel"
          onClick={close}
        >
          <Icon name="X" size="md" />
        </IconButton>
      </div>

      {/* The only announcement channel: phase changes are otherwise purely visual. */}
      <p className="rc-dictate-sheet__sr" aria-live="polite" role="status">
        {recording ? `Recording, ${mmss(elapsedMs)} elapsed.` : STATUS_TEXT[phase] ?? ""}
      </p>

      {/* Everything above the action scrolls; the action itself never does. */}
      <div className="rc-dictate-sheet__body">
        {phase !== "review" && (
          <>
            {/* Above the example and OUTSIDE it: it is an instruction about how
                to speak, not part of the sentence being illustrated. Inside the
                quoted block it read as a third line of the example. */}
            {schema.hint && <p className="rc-dictate-hint">{schema.hint}</p>}

            {/* A worked example, fully populated, with the swappable parts
                marked. A filled sentence is understood at a glance; a row of
                empty placeholders has to be decoded first. */}
            <blockquote className="rc-dictate-template">
              {schema.exampleLabel && (
                <span className="rc-dictate-template__label">{schema.exampleLabel}</span>
              )}
              {schema.template.map((part, i) =>
                typeof part === "string" ? (
                  <span key={i}>{part}</span>
                ) : (
                  <mark key={i} className="rc-dictate-template__slot">{part.value}</mark>
                )
              )}
            </blockquote>
          </>
        )}

        {phase === "error" && (
          <div className="rc-dictate-error" role="alert">
            <p className="rc-dictate-error__msg">{errorMessage}</p>
            <ButtonGroup align="stretch" className="rc-dictate-sheet__actions">
              {errorKind === "network" && canRetry && (
                <Button variant="primary" data-btn="dictation-retry" onClick={retry}>
                  Try again
                </Button>
              )}
              {errorKind !== "network" && errorKind !== "permission" && (
                <Button variant="primary" data-btn="dictation-record-again" onClick={start}>
                  Record again
                </Button>
              )}
              <Button
                variant="secondary"
                data-btn="dictation-search"
                onClick={() => onSearchManually(result)}
              >
                Search manually
              </Button>
            </ButtonGroup>
          </div>
        )}

        {/* BEFORE recording only. As a prompt — "these are the things to
            mention" — it earns its place. In the review step it did not: the
            editable fields below restate the same seven rows, and they show the
            value AND let you correct it, where the checklist could only report.
            Heard-vs-missing is carried there by the per-field ✓ / ○ markers. */}
        {!swept && !done && schema.checklist?.length > 0 && (
          <ul className="rc-dictate-check">
            {schema.checklist.map((item, i) => (
              <li
                key={item.key}
                className="rc-dictate-check__row"
                style={{ "--rc-dict-i": i }}
              >
                <span className="rc-dictate-check__icon" aria-hidden="true">○</span>
                <span className="rc-dictate-check__label">{item.label}</span>
              </li>
            ))}
          </ul>
        )}

        {/* FIRST in the review step, directly under the sheet heading: what the
            sheet heard is the thing every other row on the panel derives from,
            so it reads before the derivations rather than after them. Its edit
            and mic controls sit on the same line as the text. */}
        {swept && !done && (
          <div className="rc-dictate-heard">
            {editing ? (
              <textarea
                ref={draftRef}
                className="rc-dictate-heard__input"
                value={draft}
                rows={2}
                aria-label="Edit what was heard"
                onChange={(e) => setDraft(e.target.value)}
                // Autosave: the edit commits on blur and on Escape/Enter, so
                // there is no Save button to forget to press.
                onBlur={commitDraft}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    setDraft(transcript);
                    setEditing(false);
                  }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commitDraft();
                  }
                }}
              />
            ) : (
              <p className="rc-dictate-heard__text">“{transcript || "—"}”</p>
            )}
            <div className="rc-dictate-heard__tools">
              <IconButton
                size="xl"
                round
                label={editing ? "Done editing" : "Edit what was heard"}
                title={editing ? "Done editing" : "Edit what was heard"}
                data-btn="dictation-edit"
                disabled={reanalysing}
                onClick={() => (editing ? commitDraft() : setEditing(true))}
              >
                <Icon name={editing ? "Check" : "Pencil"} size="sm" />
              </IconButton>
              <IconButton
                size="xl"
                round
                label="Record again"
                title="Record again"
                data-btn="dictation-redo"
                onClick={cancel}
              >
                <Icon name="Mic" size="sm" />
              </IconButton>
            </div>
          </div>
        )}

        {/* Both states live under the line they belong to, and both are words
            rather than a spinner alone: "the fields below no longer match the
            text above" is not something a spinner can say. */}
        {swept && !done && reanalysing && (
          <p className="rc-dictate-heard__status" role="status">Re-reading the corrected text…</p>
        )}
        {swept && !done && reanalyseError && (
          <p className="rc-dictate-heard__status is-error" role="alert">{reanalyseError}</p>
        )}

        {swept && !done && extras.length > 0 && (
          <div className="rc-dictate-extras">
            <h3 className="rc-dictate-extras__title">Also heard</h3>
            <ul className="rc-dictate-extras__list">
              {extras.map((e) => (
                <li key={e.key}>
                  <span className="rc-dictate-extras__label">{e.label}</span>
                  <span className="rc-dictate-extras__value">
                    {schema.formatValue(e.key, fields)}
                  </span>
                </li>
              ))}
            </ul>
            {schema.extrasNote(fields) && (
              <p className="rc-dictate-extras__note">{schema.extrasNote(fields)}</p>
            )}
          </div>
        )}

        {swept && !done && heardMoney.length > 0 && (
          // A misheard price is the most expensive error this feature can make,
          // so every money figure is read back in words and flagged as heard.
          <div className="rc-dictate-money" role="note">
            <span className="rc-dictate-money__tag">Heard, not typed</span>
            <ul className="rc-dictate-money__list">
              {heardMoney.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
        )}

        {swept && (
          <div className="rc-dictate-result">
            {/* The transcript line moved ABOVE, directly under the heading.
                The review is EDITABLE and domain-supplied: every dictated value
                is correctable here, and the listing is created from there. */}
            {renderReview(result, { onDone: () => setDone(true) })}
          </div>
        )}
      </div>

      {capturing && (
        <div className="rc-dictate-capture">
          {recording && (
            <>
              <div className="rc-dictate-meter" aria-hidden="true">
                <div
                  className="rc-dictate-meter__bar"
                  style={{ width: `${Math.min(100, Math.round(level * 140))}%` }}
                />
              </div>
              <p className={`rc-dictate-timer${nearingLimit ? " is-warning" : ""}`}>
                {mmss(elapsedMs)} / {mmss(MAX_MS)}
                {nearingLimit ? " — stopping soon" : ""}
              </p>
            </>
          )}

          <Button
            variant="primary"
            size="lg"
            fullWidth
            data-btn="dictation-record"
            onClick={recording ? stopWithCue : startWithCue}
            disabled={busy}
            // Only on the resting label: mid-flight the button is reporting
            // progress, and a mic beside "Analysing…" would suggest it is
            // still listening.
            leadingIcon={!recording && !busy ? <Icon name="Mic" size="sm" /> : null}
          >
            {recording
              ? "Stop"
              : phase === "requesting-permission"
                ? "Waiting for permission…"
                : phase === "encoding"
                  ? "Preparing…"
                  : analysing
                    ? "Analysing…"
                    : "Start recording"}
          </Button>
        </div>
      )}
    </ReactModal>
  );
};

export default DictationOverlay;
